import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { userAPI, messageAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ServiceCard from '../components/common/ServiceCard';
import toast from 'react-hot-toast';

export default function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState('');
  const [showMsg, setShowMsg] = useState(false);

  useEffect(() => {
    userAPI.getProfile(id).then(r => setSeller(r.data)).catch(() => navigate('/404')).finally(() => setLoading(false));
  }, [id]);

  const sendMessage = async e => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    try {
      await messageAPI.send({ receiverId: id, content: msgText });
      toast.success('Message sent!');
      navigate('/messages');
    } catch { toast.error('Failed to send message'); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!seller) return null;

  const initials = `${seller.firstName?.[0] || ''}${seller.lastName?.[0] || ''}`.toUpperCase();

  return (
    <div className="page">
      <div className="container">
        {/* Profile Header */}
        <div className="seller-profile-header">
          <div className="avatar avatar-xl">
            {seller.avatar ? <img src={`http://localhost:5000${seller.avatar}`} alt={seller.firstName} /> : initials}
          </div>
          <div className="seller-profile-info">
            <h1>{seller.businessName || `${seller.firstName} ${seller.lastName}`}</h1>
            {(seller.city || seller.state) && <p className="text-light">📍 {[seller.city, seller.state, seller.country].filter(Boolean).join(', ')}</p>}
            <div className="seller-profile-stats">
              <span>📦 {seller._count?.services} Services</span>
              <span>⭐ {seller.avgRating?.toFixed(1)} Rating</span>
              <span>💬 {seller._count?.reviewsReceived} Reviews</span>
              <span>🗓️ Member since {new Date(seller.createdAt).getFullYear()}</span>
            </div>
            {seller.businessDescription && <p className="seller-profile-bio">{seller.businessDescription}</p>}
            {user && user.id !== id && (
              <button className="btn btn-outline mt-2" onClick={() => setShowMsg(!showMsg)}>💬 Contact Seller</button>
            )}
            {showMsg && (
              <form onSubmit={sendMessage} className="mt-2 flex gap-1" style={{ maxWidth: 500 }}>
                <input type="text" className="form-input" placeholder="Send a message..." value={msgText} onChange={e => setMsgText(e.target.value)} required />
                <button type="submit" className="btn btn-primary">Send</button>
              </form>
            )}
          </div>
        </div>

        {/* Services */}
        <h2 className="mt-4 mb-2">Services ({seller.services?.length})</h2>
        {seller.services?.length > 0 ? (
          <div className="grid grid-4">
            {seller.services.map(s => <ServiceCard key={s.id} service={{ ...s, seller }} />)}
          </div>
        ) : <p className="text-light">No services listed yet.</p>}

        {/* Reviews */}
        {seller.reviewsReceived?.length > 0 && (
          <>
            <h2 className="mt-4 mb-2">Reviews</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {seller.reviewsReceived.map(r => (
                <div key={r.id} className="card card-body">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="avatar">
                      {r.author.avatar ? <img src={`http://localhost:5000${r.author.avatar}`} alt={r.author.firstName} /> : `${r.author.firstName?.[0]}${r.author.lastName?.[0]}`}
                    </div>
                    <strong>{r.author.firstName} {r.author.lastName}</strong>
                    <div className="stars" style={{ marginLeft: 'auto' }}>
                      {[1,2,3,4,5].map(n => <span key={n} className={`star ${n <= r.rating ? 'filled' : ''}`}>★</span>)}
                    </div>
                  </div>
                  {r.comment && <p className="text-light">{r.comment}</p>}
                  {r.service && <p className="text-sm text-primary mt-1">Service: {r.service.title}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
